/**
 * Achievement Chart toggle for Success Criteria.
 *
 * The Math Engine emits each lesson's `## Success Criteria` as a
 * 3-column markdown table: `| Category | Success Criterion | Level 3
 * descriptor |`. This module:
 *
 *   1. Detects that table in a lesson's markdown.
 *   2. Splits the markdown into `before | tableSection | after`.
 *   3. Provides an `AchievementChartView` React component that toggles
 *      between rendering the original table (default) and a 4-card
 *      grouped layout (one card per Achievement Chart category).
 *
 * The toggle is intentionally a thin reader — the schema validation
 * happens at engine-output time, so the parser stays optimistic. If
 * the table doesn't match the expected shape, we return `null` and the
 * regular markdown rendering kicks in unchanged.
 */

// Canonical category order + display labels for the Achievement Chart.
export const ACHIEVEMENT_CATEGORIES = [
  { key: "K&U", label: "Knowledge & Understanding", short: "K&U" },
  { key: "Thinking", label: "Thinking", short: "Thinking" },
  { key: "Communication", label: "Communication", short: "Communication" },
  { key: "Application", label: "Application", short: "Application" },
];

const _CATEGORY_ALIASES = {
  "k&u": "K&U",
  "knowledge and understanding": "K&U",
  "knowledge & understanding": "K&U",
  thinking: "Thinking",
  communication: "Communication",
  application: "Application",
};

const _normaliseCategory = (raw) => {
  const t = (raw || "").trim().toLowerCase();
  return _CATEGORY_ALIASES[t] || null;
};

/**
 * Extracts the Success Criteria table from a lesson's markdown.
 *
 * Returns `{ before, after, criteria }` when a valid 3-column
 * `| Category | Success Criterion | Level 3 descriptor |` table is
 * found immediately under a `## Success Criteria` heading. Returns
 * `null` otherwise.
 */
export function extractSuccessCriteria(markdown) {
  if (!markdown) return null;
  // Locate the heading. Accept variations like "Success Criteria",
  // "Success criteria", "## Success Criteria (3-5 rows)" etc.
  const headingMatch = markdown.match(/^##\s+Success\s+Criteria\b.*$/im);
  if (!headingMatch) return null;
  const headingIdx = headingMatch.index;
  const headingEnd = headingIdx + headingMatch[0].length;
  // The rest of the doc starts where the heading ends.
  const tail = markdown.slice(headingEnd);
  // Skip blank lines until we find either the table header row OR a
  // new `## ` heading (which would mean no table).
  const tableHeaderMatch = tail.match(
    /\n+\|\s*Category\s*\|\s*Success Criterion\s*\|\s*Level\s*3\s*descriptor\s*\|\s*\n\|[-\s|]+\|\s*\n/i,
  );
  if (!tableHeaderMatch) return null;
  const tableStart = headingEnd + tableHeaderMatch.index;
  // Walk forward collecting `|...|` rows until we hit a blank line, a
  // `#` heading, or EOF.
  const afterHeader =
    headingEnd + tableHeaderMatch.index + tableHeaderMatch[0].length;
  const rest = markdown.slice(afterHeader);
  const lines = rest.split("\n");
  const rows = [];
  let consumed = 0;
  for (const line of lines) {
    if (line.trim() === "" || line.startsWith("#")) break;
    if (!line.trim().startsWith("|")) break;
    const cells = line
      .replace(/^\s*\|/, "")
      .replace(/\|\s*$/, "")
      .split("|")
      .map((c) => c.trim());
    if (cells.length < 3) break;
    const category = _normaliseCategory(cells[0]);
    if (!category) {
      // Unknown category label — bail; let regular markdown render it.
      consumed += line.length + 1;
      continue;
    }
    rows.push({
      category,
      criterion: cells[1],
      level3: cells[2] || "",
    });
    consumed += line.length + 1;
  }
  if (rows.length === 0) return null;

  const tableEnd = afterHeader + consumed;
  // Strip a trailing blank line so the rejoined markdown doesn't get
  // double newlines around the toggle.
  let endIdx = tableEnd;
  while (markdown.charAt(endIdx) === "\n") endIdx += 1;

  return {
    before: markdown.slice(0, headingIdx),
    after: markdown.slice(endIdx),
    criteria: rows,
    headingText: headingMatch[0].replace(/^##\s+/, "").trim(),
    rawTable: markdown.slice(tableStart, tableEnd),
  };
}
